import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';

import { Select } from 'core/components/select';
import { track } from 'core/helpers/analytics';

/**
 * #30919: Forreston/CFM farm Home & Rental record whether the insured also carries an auto
 * policy and, if so, whether it's with BIX or a company OTHER than BIX. This single control owns
 * two persisted fields — `global.multiPolicyDiscount` (bundle on/off) and the new
 * `global.autoBundleCarrier` ('BIX' | 'Other') — and drives the farm rater's 3-level
 * `auto_homeYN` (0 none / 1 BIX / 2 other). It is a dropdown rather than a yes/no toggle so the
 * "other carrier" level is selectable; shared by the offer discounts card and the policy screen.
 */

export const AUTO_BUNDLE_NONE = 'none';
export const AUTO_BUNDLE_BIX = 'BIX';
export const AUTO_BUNDLE_OTHER = 'Other';

export const AUTO_BUNDLE_OPTIONS = [
  { id: AUTO_BUNDLE_NONE, value: 'No auto policy' },
  { id: AUTO_BUNDLE_BIX, value: 'Auto policy with BIX' },
  { id: AUTO_BUNDLE_OTHER, value: 'Auto policy with other carrier' }
];

/**
 * Collapse the two stored fields into the single dropdown value. Bundle off → none. Bundle on
 * with the explicit 'Other' sentinel → other. Anything else with the bundle on → BIX — existing
 * policies have the bundle on with no carrier stored and must read (and keep rating) as BIX.
 */
export const autoBundleValueFrom = (multiPolicyDiscount, autoBundleCarrier) => {
  if (!multiPolicyDiscount) {
    return AUTO_BUNDLE_NONE;
  }
  return autoBundleCarrier === AUTO_BUNDLE_OTHER ? AUTO_BUNDLE_OTHER : AUTO_BUNDLE_BIX;
};

const AutoBundleSelect = ({ label, mode, disabled, onAfterChange }) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const multiPolicyDiscount = values?.global?.multiPolicyDiscount;
  const autoBundleCarrier = values?.global?.autoBundleCarrier;
  const value = autoBundleValueFrom(multiPolicyDiscount, autoBundleCarrier);

  const handleChange = useCallback(
    (_name, selected) => {
      const bundleOn = selected !== AUTO_BUNDLE_NONE;
      // Only ever persist a carrier while the bundle is on; clear it otherwise so an off bundle
      // can never rate as level 1/2.
      const carrier = bundleOn ? selected : null;
      setFieldValue('global.multiPolicyDiscount', bundleOn);
      setFieldValue('global.autoBundleCarrier', carrier);
      // Mark touched so the "Preview policy changes" / update footer appears (it gates on
      // Object.keys(touched).length); setFieldValue alone never marks a field touched.
      setFieldTouched('global.multiPolicyDiscount', true, false);
      setFieldTouched('global.autoBundleCarrier', true, false);
      track('Auto/Home Bundle Discount Changed', { value: selected });
      onAfterChange?.(bundleOn, carrier);
    },
    [setFieldValue, setFieldTouched, onAfterChange]
  );

  return (
    <Select
      id="autoBundleCarrier"
      name="autoBundleCarrier"
      label={label}
      mode={mode}
      options={AUTO_BUNDLE_OPTIONS}
      value={value}
      onChange={handleChange}
      disabled={disabled}
      fullWidth
    />
  );
};

AutoBundleSelect.propTypes = {
  label: PropTypes.string,
  mode: PropTypes.oneOf(['light', 'dark']),
  disabled: PropTypes.bool,
  // Fired after both Formik fields are written, with (bundleOn, carrier); used by the offer card
  // to mirror the change into the offer store.
  onAfterChange: PropTypes.func
};

AutoBundleSelect.defaultProps = {
  label: 'Auto/Home bundle discount',
  mode: 'light',
  disabled: false,
  onAfterChange: undefined
};

export default AutoBundleSelect;
